import { useState, useMemo, useContext } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import TradeAnalysisMain from "./trade-analysis-main.tsx";
import useLoadIssueIntoDealBuilder from "../deal-builder/hooks/useLoadIssueIntoDealBuilder.ts";
import useDealBuilderResultStore from "../stores/deal-builder-result.store.ts";
import { ConfirmationDialog } from "../pricing-management/pages/deals-page/delete-dilog.tsx";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarController,
  BarElement,
  ScatterController,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-moment";
import { Chart } from "react-chartjs-2";
import useAnalysis, { PrimaryErrorEntry } from "./hooks/useAnalysis.ts";
import { formatCusip } from "../tools/cusips.ts";
import { UserInfoContext } from "../contexts/UserInfoContext.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarController,
  BarElement,
  ScatterController,
  TimeScale,
  Tooltip,
  Legend
);

type SearchMode = "cusip" | "offering_date";
type ChartMode = "error" | "abs_error";

interface IssueGroup {
  issue_id: number;
  bonds: PrimaryErrorEntry[];
}

function groupByIssueId(data: PrimaryErrorEntry[]): IssueGroup[] {
  const map = new Map<number, PrimaryErrorEntry[]>();
  for (const entry of data) {
    const existing = map.get(entry.issue_id);
    if (existing) {
      existing.push(entry);
    } else {
      map.set(entry.issue_id, [entry]);
    }
  }
  return Array.from(map.entries()).map(([issue_id, bonds]) => ({ issue_id, bonds }));
}

function YieldChart({ data }: { data: PrimaryErrorEntry[] }) {
  const sorted = useMemo(() =>
    [...data]
      .filter((d) => d.maturity_date)
      .sort((a, b) => moment(a.maturity_date).valueOf() - moment(b.maturity_date).valueOf()),
    [data]
  );

  const chartData = useMemo(() => ({
    datasets: [
      {
        label: "Predicted Yield",
        data: sorted.map((d) => ({ x: moment.utc(d.maturity_date).valueOf(), y: d.predicted_yield })),
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: "rgba(54, 162, 235, 0.7)",
        pointRadius: 5,
      },
      {
        label: "Offering Yield",
        data: sorted.map((d) => ({ x: moment.utc(d.maturity_date).valueOf(), y: d.offering_yield })),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.7)",
        pointRadius: 5,
      },
    ],
  }), [sorted]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time" as const,
        time: { unit: "year" as const, displayFormats: { year: "YYYY" } },
        title: { display: true, text: "Maturity Date" },
      },
      y: {
        title: { display: true, text: "Yield" },
      },
    },
    plugins: {
      tooltip: {
        titleFont: { size: 14 },
        bodyFont: { size: 14 },
        callbacks: {
          title: (items: any[]) => moment.utc(items[0].parsed.x).format("MM/DD/YYYY"),
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(3)}`,
        },
      },
    },
  }), []);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="subtitle1" mb={2}>Yields by Maturity Date</Typography>
      <Box sx={{ height: 300 }}>
        <Chart type="scatter" data={chartData} options={options as any} />
      </Box>
    </Paper>
  );
}

function ErrorBarChart({ data, chartMode, onChartModeChange }: {
  data: PrimaryErrorEntry[];
  chartMode: ChartMode;
  onChartModeChange: (mode: ChartMode) => void;
}) {
  const sorted = useMemo(() =>
    [...data]
      .filter((d) => d.maturity_date)
      .sort((a, b) => moment(a.maturity_date).valueOf() - moment(b.maturity_date).valueOf()),
    [data]
  );

  const chartData = useMemo(() => ({
    datasets: [
      {
        type: "bar" as const,
        label: chartMode === "error" ? "Error" : "Abs Error",
        data: sorted.map((d) => ({
          x: moment.utc(d.maturity_date).valueOf(),
          y: chartMode === "error" ? d.error : Math.abs(d.error),
        })),
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.5)",
      },
    ],
  }), [sorted, chartMode]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time" as const,
        time: { unit: "year" as const, displayFormats: { year: "YYYY" } },
        title: { display: true, text: "Maturity Date" },
      },
      y: {
        title: { display: true, text: chartMode === "error" ? "Error" : "Abs Error" },
      },
    },
    plugins: {
      tooltip: {
        titleFont: { size: 14 },
        bodyFont: { size: 14 },
        callbacks: {
          title: (items: any[]) => moment.utc(items[0].parsed.x).format("MM/DD/YYYY"),
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(3)}`,
        },
      },
    },
  }), [chartMode]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">Error by Maturity Date</Typography>
        <ToggleButtonGroup
          size="small"
          value={chartMode}
          exclusive
          onChange={(_, v) => { if (v) onChartModeChange(v); }}
        >
          <ToggleButton value="error">Error</ToggleButton>
          <ToggleButton value="abs_error">Abs Error</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ height: 300 }}>
        <Chart type="bar" data={chartData} options={options as any} />
      </Box>
    </Paper>
  );
}

function IssueRow({ group, onLoad }: { group: IssueGroup, onLoad: (group: IssueGroup) => void }) {
  const [open, setOpen] = useState(false);
  const first = group.bonds[0];
  const hasMultiple = group.bonds.length > 1;
  const avgError = group.bonds.reduce((sum, b) => sum + b.error, 0) / group.bonds.length;
  const absAvgError = group.bonds.reduce((sum, b) => sum + Math.abs(b.error), 0) / group.bonds.length;

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: hasMultiple && open ? "unset" : undefined } }}>
        <TableCell sx={{ width: 50 }}>
          {hasMultiple && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{group.issue_id}</TableCell>
        <TableCell>{first.issuer_long_name}</TableCell>
        <TableCell>{first.state}</TableCell>
        <TableCell>{first.cusip}</TableCell>
        <TableCell align="right">{first.coupon}</TableCell>
        <TableCell>{first.spline_lt_rating_composite}</TableCell>
        <TableCell>{first.maturity_date ? moment.utc(first.maturity_date).format("MM/DD/YYYY") : ""}</TableCell>
        <TableCell align="right">{avgError.toFixed(3)}</TableCell>
        <TableCell align="right">{absAvgError.toFixed(3)}</TableCell>
        <TableCell align="center" sx={{ width: 50 }}>
          <MuiTooltip title="Load this issue into the Deal Builder">
            <IconButton size="small" color="primary" onClick={() => onLoad(group)}>
              <VisibilityIcon />
            </IconButton>
          </MuiTooltip>
        </TableCell>
      </TableRow>
      {hasMultiple && (
        <TableRow>
          <TableCell sx={{ py: 0 }} colSpan={11}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ mx: 2, my: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  All bonds in issue {group.issue_id} ({group.bonds.length} bonds)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CUSIP</TableCell>
                      <TableCell>Issuer</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell align="right">Coupon</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Maturity Date</TableCell>
                      <TableCell align="right">Offering Yield</TableCell>
                      <TableCell align="right">Predicted Yield</TableCell>
                      <TableCell align="right">Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.bonds.map((bond, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{bond.cusip}</TableCell>
                        <TableCell>{bond.issuer_long_name}</TableCell>
                        <TableCell>{bond.state}</TableCell>
                        <TableCell align="right">{bond.coupon}</TableCell>
                        <TableCell>{bond.spline_lt_rating_composite}</TableCell>
                        <TableCell>{bond.maturity_date ? moment.utc(bond.maturity_date).format("MM/DD/YYYY") : ""}</TableCell>
                        <TableCell align="right">{bond.offering_yield?.toFixed(3)}</TableCell>
                        <TableCell align="right">{bond.predicted_yield?.toFixed(3)}</TableCell>
                        <TableCell align="right">{bond.error?.toFixed(3)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function PrimaryErrorsTab() {
  const { fetchErrors, loading, data, error } = useAnalysis();

  const [searchMode, setSearchMode] = useState<SearchMode>("cusip");
  const [cusip, setCusip] = useState("");
  const [offeringDate, setOfferingDate] = useState<Moment | null>(null);
  const [chartMode, setChartMode] = useState<ChartMode>("abs_error");
  const [lastSearchMode, setLastSearchMode] = useState<SearchMode>("cusip");

  const { loadIssue } = useLoadIssueIntoDealBuilder();
  const { DealBuilderService } = useDealBuilderResultStore();
  const [pendingIssue, setPendingIssue] = useState<IssueGroup | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  const groups = useMemo(() => groupByIssueId(data), [data]);
  const showCharts = !loading && data.length > 0 && lastSearchMode === "cusip";

  const handleSearch = () => {
    setLastSearchMode(searchMode);
    if (searchMode === "cusip" && cusip.trim()) {
      fetchErrors({ cusip: formatCusip(cusip) });
    } else if (searchMode === "offering_date" && offeringDate?.isValid()) {
      fetchErrors({ offering_date: offeringDate.format("YYYY-MM-DD") });
    }
  };

  async function runLoad(group: IssueGroup) {
    const result = await loadIssue(group.bonds);
    if (!result.valid) setLoadError(result.error);
  }

  // Loading replaces whatever is in the deal builder, so only ask when there
  // is actually something there to lose.
  function onLoadIssue(group: IssueGroup) {
    if (DealBuilderService) return setPendingIssue(group);
    runLoad(group);
  }

  function onConfirmOverwrite() {
    const group = pendingIssue;
    setPendingIssue(undefined);
    if (group) runLoad(group);
  }

  const canSearch =
    (searchMode === "cusip" && (cusip.trim().length === 8 || cusip.trim().length === 9)) ||
    (searchMode === "offering_date" && offeringDate?.isValid());

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Primary Errors Analysis
        </Typography>

        <Stack direction="row" alignItems="center" gap={3}>
          <RadioGroup
            row
            value={searchMode}
            onChange={(e) => setSearchMode(e.target.value as SearchMode)}
          >
            <FormControlLabel value="cusip" control={<Radio />} label="CUSIP" />
            <FormControlLabel value="offering_date" control={<Radio />} label="Offering Date" />
          </RadioGroup>

          {searchMode === "cusip" ? (
            <TextField
              label="CUSIP"
              size="small"
              value={cusip}
              onChange={(e) => setCusip(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canSearch) handleSearch(); }}
              sx={{ width: 250 }}
            />
          ) : (
            <DatePicker
              label="Offering Date"
              value={offeringDate}
              onChange={(v) => setOfferingDate(v)}
              slotProps={{ textField: { size: "small", sx: { width: 250 } } }}
            />
          )}

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={!canSearch || loading}
            sx={{ color: "white" }}
          >
            Search
          </Button>
        </Stack>
      </Paper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && data.length > 0 && (
        <Box sx={{
          display: "flex",
          flexDirection: showCharts ? { xs: "column", xl: "row" } : "column",
          gap: 3,
          alignItems: "flex-start",
        }}>
          <TableContainer component={Paper} sx={{ flex: 1, minWidth: 0 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ height: 60 }}>
                  <TableCell />
                  <TableCell>Issue ID</TableCell>
                  <TableCell>Issuer</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>CUSIP</TableCell>
                  <TableCell align="right">Coupon</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Maturity Date</TableCell>
                  <TableCell align="right">Avg Error</TableCell>
                  <TableCell align="right">Abs Avg Error</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <IssueRow key={group.issue_id} group={group} onLoad={onLoadIssue} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {showCharts && (
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <YieldChart data={data} />
              <ErrorBarChart data={data} chartMode={chartMode} onChartModeChange={setChartMode} />
            </Box>
          )}
        </Box>
      )}

      {!loading && !error && data.length === 0 && groups.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <Typography color="text.secondary">
            Search by CUSIP or offering date to view primary errors.
          </Typography>
        </Box>
      )}

      <ConfirmationDialog
        title="Replace the deal in the Deal Builder?"
        description="The deal currently in the Deal Builder will be replaced. Any unsaved changes will be lost."
        acceptLabel="Replace"
        acceptColor="warning"
        open={pendingIssue !== undefined}
        onAccept={onConfirmOverwrite}
        onClose={() => setPendingIssue(undefined)}
      />

      <Snackbar
        open={loadError !== undefined}
        autoHideDuration={6000}
        onClose={() => setLoadError(undefined)}
        message={loadError}
      />
    </Box>
  );
}

export default function AnalysisMain() {
  const [activeTab, setActiveTab] = useState(0);
  const { isEmployee } = useContext(UserInfoContext);

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, bgcolor: "white" }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Primary Errors" />
          {isEmployee() && <Tab label="Deal Performance" />}
        </Tabs>
      </Box>
      {activeTab === 0 && <PrimaryErrorsTab />}
      {activeTab === 1 && <TradeAnalysisMain />}
    </Box>
  );
}
